'use client';

import {
  ReactNode, useCallback, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import NiceModal from '@ebay/nice-modal-react';
import { MicrophoneIcon, PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { Button } from '@components/common';
import { useChatComposer } from '@hooks/useChatComposer/useChatComposer';
import { useAutoResizeTextarea } from '@hooks/useAutoResizeTextarea';
import { BestieVoiceModal } from '@components/features/modals/gemini-voice';
import { useBestieChatStateOptional } from '@components/contexts/BestieChatStateContext';

interface ChatBranchProps {
  onSubmit: (text: string) => Promise<void>;
  leftSlot?: ReactNode;
  /** Override the empty-input mic action (default: open the Bestie voice modal). */
  onMic?: () => void;
  /** Extra control rendered after the mic/send button (e.g. a video-answer button). */
  rightSlot?: ReactNode;
  /** Override the default "What are your career plans?" placeholder/aria-label. */
  placeholder?: string;
}

export const ChatBranch = ({
  onSubmit, leftSlot, onMic, rightSlot, placeholder,
}: ChatBranchProps) => {
  const { t } = useTranslation('common');
  const composer = useChatComposer({ onSubmit });
  const [isEditing, setIsEditing] = useState(false);
  // useChatComposer clears the input immediately (before awaiting onSubmit), so hasText alone
  // would flip the button back to the empty/mic state while the save is still in flight — track
  // the pending save separately so the button shows a spinner instead until it actually resolves.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const chatState = useBestieChatStateOptional();

  // Open the input ready to type when the user arrived here via the prompt-bar
  // "coaching" action (which set a one-shot request on the shared MC state).
  // Read-and-clear once on mount; the focus effect below then focuses it.
  const consumeChatFocusRequest = chatState?.consumeChatFocusRequest;
  useEffect(() => {
    if (consumeChatFocusRequest?.()) setIsEditing(true);
  }, [consumeChatFocusRequest]);

  // Set immediately before a send so the collapse-on-empty effect can tell a
  // submit-clear apart from a manual delete-all and avoid unmounting the
  // textarea (which would drop focus after every message).
  const justSentRef = useRef(false);

  const handleSend = useCallback(async () => {
    if (composer.input.trim().length === 0 || isSubmitting) return;
    justSentRef.current = true;
    setIsSubmitting(true);
    try {
      await composer.submit();
    } finally {
      setIsSubmitting(false);
    }
    // Keep the textarea mounted and focused so the user can keep chatting
    // without re-clicking the input after every message.
    composer.textareaRef.current?.focus();
  }, [composer, isSubmitting]);

  const { resize } = useAutoResizeTextarea({ ref: composer.textareaRef, value: composer.input, maxRows: 3 });

  useEffect(() => {
    if (!isEditing) return;
    const el = composer.textareaRef.current;
    if (!el) return;
    el.focus();
    const len = el.value.length;
    el.setSelectionRange(len, len);
    resize();
  }, [isEditing, resize, composer.textareaRef]);

  // Collapse back to the fake-input view when the user clears their text.
  const prevInputRef = useRef(composer.input);
  useEffect(() => {
    // A send clears the input too — do NOT collapse on it (that would unmount
    // the textarea and drop focus). Only collapse on a manual delete-all.
    if (justSentRef.current) {
      justSentRef.current = false;
      prevInputRef.current = composer.input;
      return;
    }
    if (isEditing && prevInputRef.current.length > 0 && composer.input.length === 0) {
      setIsEditing(false);
    }
    prevInputRef.current = composer.input;
  }, [isEditing, composer.input]);

  const hasText = composer.input.trim().length > 0;
  // The placeholder is audience-specific: asking someone posting a job about their career plans reads as
  // nonsense. The Bestie chat supplies an employer-aware key; an explicit `placeholder` prop still wins,
  // and the other `ChatBranch` callers (stories answer bar, interview answer bar) have no chat state and
  // keep the candidate copy.
  const inputPlaceholder = placeholder ?? t(chatState?.inputPlaceholderKey ?? 'homepage.chat.input.placeholder');

  const actionButton = (
    <Button
      type="button"
      styling="outline"
      color="light"
      rounding="none"
      isLoading={isSubmitting}
      aria-label={hasText ? t('global.label.send', 'Send') : t('global.label.voice', 'Voice')}
      className="ml-auto h-9! w-9! shrink-0 rounded-lg! border-none! bg-secondary p-0! transition-transform hover:scale-110 hover:bg-secondary! lg:h-10! lg:w-10!"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => {
        if (hasText) void handleSend();
        else if (onMic) onMic();
        else void NiceModal.show(BestieVoiceModal);
      }}
    >
      {isSubmitting ? null : (hasText
        ? <PaperAirplaneIcon className="size-5 text-white" />
        : <MicrophoneIcon className="size-5 text-white" />)}
    </Button>
  );

  if (!isEditing) {
    return (
      <form
        onSubmit={(e) => { e.preventDefault(); void handleSend(); }}
        className="flex w-full items-center gap-2"
      >
        {leftSlot}
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          aria-label={inputPlaceholder}
          className="flex min-w-0 flex-1 px-2 py-1 text-left text-xs leading-normal md:text-sm"
        >
          {composer.input
            ? <span className="block w-full truncate text-ink">{composer.input}</span>
            : <span className="block w-full truncate text-ink/60">{inputPlaceholder}</span>}
        </button>
        {actionButton}
        {!hasText && !isSubmitting && rightSlot}
      </form>
    );
  }

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); void handleSend(); }}
      className="flex w-full flex-col gap-1"
    >
      <textarea
        ref={composer.textareaRef}
        rows={1}
        value={composer.input}
        onChange={composer.onChange}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void handleSend();
            return;
          }
          composer.onKeyDown(e);
        }}
        onBlur={() => setIsEditing(false)}
        aria-label={inputPlaceholder}
        className="block w-full resize-none border-none bg-transparent px-2 py-1 text-base leading-normal text-ink shadow-none outline-none focus:ring-0 focus:ring-offset-0 md:text-sm"
      />

      <div className="flex w-full items-center gap-2">
        {leftSlot}
        {actionButton}
        {!hasText && !isSubmitting && rightSlot}
      </div>
    </form>
  );
};
